/**
 * useMediaQuery Hook
 *
 * A custom hook for responsive design that listens to media query changes.
 * Used primarily to detect mobile devices for adaptive UI rendering.
 */
import { useEffect, useState } from "react";

/**
 * Hook to detect if a media query matches
 *
 * @param query - CSS media query string (e.g., "(max-width: 768px)")
 * @returns boolean indicating if the query matches
 *
 * @example
 * const isMobile = useMediaQuery("(max-width: 768px)");
 * const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
 */
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState<boolean>(() => {
    // Check if window is defined (SSR safety)
    if (typeof window !== "undefined") {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia(query);

    // Set initial value
    setMatches(mediaQuery.matches);

    // Create event listener
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Add listener using the modern API with fallback
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [query]);

  return matches;
};

/**
 * Common breakpoint queries
 */
export const BREAKPOINTS = {
  mobile: "(max-width: 639px)",
  tablet: "(min-width: 640px) and (max-width: 1023px)",
  desktop: "(min-width: 1024px)",
  prefersReducedMotion: "(prefers-reduced-motion: reduce)",
  prefersDarkMode: "(prefers-color-scheme: dark)",
} as const;

/**
 * Convenience hook for mobile detection
 *
 * @returns boolean indicating if the viewport is mobile-sized
 */
export const useIsMobile = (): boolean => {
  return useMediaQuery(BREAKPOINTS.mobile);
};

/**
 * Convenience hook for tablet detection
 *
 * @returns boolean indicating if the viewport is tablet-sized
 */
export const useIsTablet = (): boolean => {
  return useMediaQuery(BREAKPOINTS.tablet);
};

/**
 * Convenience hook for desktop detection
 *
 * @returns boolean indicating if the viewport is desktop-sized
 */
export const useIsDesktop = (): boolean => {
  return useMediaQuery(BREAKPOINTS.desktop);
};
